import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '@lipaflow/shared'
import toast from 'react-hot-toast'
import type { StepProps } from '../types'
import type { CreateUserResponse, ResendUserInviteRequest } from '@lipaflow/shared'

export default function CreateUserStep({ tenantId }: StepProps) {
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    phone: '',
    send_invite: true,
  })

  const showInviteResult = (data: CreateUserResponse) => {
    const base =
      data.was_existing_user
        ? 'Existing admin user reused.'
        : 'Admin user created.'

    if (data.delivery_status === 'sent') {
      // Accepted by the email provider; a bounce shows on the invitation later.
      toast.success(`${base} Invitation email sent.`)
      return
    }

    if (data.delivery_status === 'skipped') {
      toast.success(`${base} ${data.delivery_error || 'Invitation was skipped.'}`)
      return
    }

    // The link only ever goes to their inbox: fix the address or the email
    // setup, then resend.
    toast.error(
      data.delivery_error
        ? `${base} The invitation email failed: ${data.delivery_error}. Check the address, then resend.`
        : `${base} The invitation email was not sent. Check the address, then resend.`
    )
  }

  const createMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        tenant_id: tenantId,
        email: formData.email.trim(),
        full_name: formData.full_name.trim(),
        phone: formData.phone.trim() || undefined,
        send_invite: formData.send_invite,
      }
      const res = await api.post<CreateUserResponse>('/admin/wizard/create-user', payload)
      return res.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-wizard', tenantId] })
      showInviteResult(data)
    },
    onError: (err: unknown) => {
      toast.error(apiErrorMessage(err, 'Failed to create user'))
    },
  })

  const resendMutation = useMutation({
    mutationFn: async () => {
      const payload: ResendUserInviteRequest = {
        tenant_id: tenantId,
        email: formData.email.trim() || undefined,
      }
      const res = await api.post<CreateUserResponse>('/admin/wizard/resend-user-invite', payload)
      return res.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-wizard', tenantId] })
      showInviteResult(data)
    },
    onError: (err: unknown) => {
      toast.error(apiErrorMessage(err, 'Failed to resend invite'))
    },
  })

  const skipMutation = useMutation({
    mutationFn: async () => {
      await api.post('/admin/wizard/skip-user', {
        tenant_id: tenantId,
        reason: 'Admin user will be created later',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-wizard', tenantId] })
      toast.success('Skipped for now. Continue with data import.')
    },
    onError: (err: unknown) => {
      toast.error(apiErrorMessage(err, 'Failed to skip user creation'))
    },
  })

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Step 5: Add their first administrator</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          createMutation.mutate()
        }}
        className="space-y-4"
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink">Full name *</label>
            <input
              type="text"
              required
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              className="mt-1 block w-full border border-hairline rounded-md shadow-sm py-2 px-3"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink">Email *</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value.toLowerCase() })}
              className="mt-1 block w-full border border-hairline rounded-md shadow-sm py-2 px-3"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="mt-1 block w-full border border-hairline rounded-md shadow-sm py-2 px-3"
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="send_invite"
              checked={formData.send_invite}
              onChange={(e) => setFormData({ ...formData, send_invite: e.target.checked })}
              className="mr-2"
            />
            <label htmlFor="send_invite" className="text-sm text-ink">
              Send invite email
            </label>
          </div>
        </div>
        <div className="flex justify-end pt-4">
          <button
            type="button"
            disabled={skipMutation.isPending || resendMutation.isPending || createMutation.isPending}
            onClick={() => skipMutation.mutate()}
            className="mr-3 px-6 py-2 text-ink-muted hover:text-ink disabled:opacity-50"
          >
            {skipMutation.isPending ? 'Skipping...' : 'Skip for now'}
          </button>
          <button
            type="button"
            disabled={
              skipMutation.isPending
              || resendMutation.isPending
              || createMutation.isPending
              || !formData.email.trim()
            }
            onClick={() => resendMutation.mutate()}
            className="mr-3 px-6 py-2 border border-accent text-accent rounded-button hover:bg-accent-soft disabled:opacity-50"
          >
            {resendMutation.isPending ? 'Resending...' : 'Resend Invite'}
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="px-6 py-2 bg-accent text-on-accent rounded-button hover:bg-accent-hover disabled:opacity-50"
          >
            {createMutation.isPending ? 'Creating...' : 'Create User'}
          </button>
        </div>
      </form>
    </div>
  )
}
